// PuzzlesPage.jsx — tsume (mat) bulmacaları listesi, zorluğa göre filtrelenebilir.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import MiniBoard from '../components/MiniBoard';
import { PUZZLES } from '../lib/puzzles';
import { Shogi } from '../lib/ShogiEngine';
import { getSolved } from '../lib/puzzleProgress';
import { useLang } from '../i18n';
import { SITE_URL } from '../config/links';

const LEVEL_COLOR = { easy: 'var(--c-puzzle)', medium: 'var(--c-academy)', hard: 'var(--c-play)' };

export const PUZZLE_TEXT = {
  tr: {
    title: 'Şogi Bulmacaları — Tsume Mat Problemleri',
    short: 'Bulmacalar',
    seoDesc: '1, 3 ve 5 hamlede mat şogi bulmacaları (tsume şogi). Kolay, orta ve zor seviyede interaktif problemler; ipucu ve adım adım çözüm.',
    lead: 'Her bulmacada Sente oynar ve her hamlede şah çekerek rakip şahı mat eder. Rakip her zaman en uzun direnen savunmayı yapar.',
    all: 'Tümü',
    mateIn: '{n} hamlede mat',
    solve: 'Çöz',
    solved: 'Çözüldü',
    progress: '{a} / {b} çözüldü',
    howTitle: 'Tsume şogi nasıl çözülür?',
    how: [
      'Sente olarak oynarsın; tahtanın altındaki taraf sensin.',
      'Her hamlende şah çekmek zorundasın. Şah çekmeyen hamle tsume kurallarına aykırıdır.',
      'Elindeki taşları sürmek çoğu zaman anahtardır. Uchifuzume (piyade sürerek mat) yasaktır.',
      'Yanlış hamlede bulmaca bekler; tekrar deneyebilir, ipucu alabilir ya da çözümü görebilirsin.',
    ],
  },
  en: {
    title: 'Shogi Puzzles — Tsume Mate Problems',
    short: 'Puzzles',
    seoDesc: 'Interactive shogi mate puzzles (tsume shogi): mate in 1, 3 and 5 at easy, medium and hard levels, with hints and step-by-step solutions.',
    lead: 'In every puzzle you play Sente and must give check on every move until the king is mated. The defender always plays the longest resistance.',
    all: 'All',
    mateIn: 'Mate in {n}',
    solve: 'Solve',
    solved: 'Solved',
    progress: '{a} / {b} solved',
    howTitle: 'How to solve tsume shogi',
    how: [
      'You play Sente — the side at the bottom of the board.',
      'Every move you make must give check. Non-checking moves break tsume rules.',
      'Dropping pieces from your hand is often the key. Mate by pawn drop (uchifuzume) is illegal.',
      'After a wrong move the puzzle waits; try again, take a hint or reveal the solution.',
    ],
  },
};

export default function PuzzlesPage() {
  const { lang, to, t } = useLang();
  const c = PUZZLE_TEXT[lang];
  const [filter, setFilter] = useState('all');
  const solved = useMemo(() => getSolved(), []);

  const boards = useMemo(() => {
    const out = {};
    PUZZLES.forEach((p) => {
      try {
        out[p.id] = Shogi.fromSfen(p.sfen).board();
      } catch {
        out[p.id] = null;
      }
    });
    return out;
  }, []);

  const levels = ['easy', 'medium', 'hard'].filter((lv) => PUZZLES.some((p) => p.level === lv));
  const list = PUZZLES.filter((p) => filter === 'all' || p.level === filter);
  const solvedCount = PUZZLES.filter((p) => solved.has(p.id)).length;

  const ld = [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: c.title,
      itemListElement: PUZZLES.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: SITE_URL + to('puzzle', { id: p.id }),
        name: p.title[lang],
      })),
    },
    breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: c.short, path: to('puzzles') }]),
  ];

  return (
    <>
      <Seo title={c.title} description={c.seoDesc} jsonLd={ld} />
      <PageHeader title={c.title} lead={c.lead} crumbs={[{ label: c.short }]} />
      <div className="container">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="chip-row" role="tablist">
            <button type="button" className={`chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
              {c.all} <span className="count">{PUZZLES.length}</span>
            </button>
            {levels.map((lv) => (
              <button
                key={lv}
                type="button"
                className={`chip ${filter === lv ? 'active' : ''}`}
                style={{ '--c': LEVEL_COLOR[lv] }}
                onClick={() => setFilter(lv)}
              >
                {t(`level.${lv}`)} <span className="count">{PUZZLES.filter((p) => p.level === lv).length}</span>
              </button>
            ))}
          </div>
          <span className="tag easy">{c.progress.replace('{a}', solvedCount).replace('{b}', PUZZLES.length)}</span>
        </div>

        <div className="row g-3 g-md-4">
          {list.map((p, i) => (
            <div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={p.id}>
              <Link to={to('puzzle', { id: p.id })} className="game-card" style={{ '--c': LEVEL_COLOR[p.level] }}>
                {solved.has(p.id) && <span className="solved-badge">✓ {c.solved}</span>}
                <span className="badge-mode">#{PUZZLES.indexOf(p) + 1}</span>
                <div className="puzzle-thumb">{boards[p.id] && <MiniBoard board={boards[p.id]} />}</div>
                <h2 className="h4 mb-1">{p.title[lang]}</h2>
                <div className="tags mt-2">
                  <span className={`tag ${p.level}`}>{t(`level.${p.level}`)}</span>
                  <span className="tag">{c.mateIn.replace('{n}', p.mateIn)}</span>
                </div>
                <span className="link-arrow">{c.solve}</span>
              </Link>
            </div>
          ))}
        </div>

        <section className="panel prose mt-4">
          <h2>{c.howTitle}</h2>
          <ul className="mb-0">{c.how.map((h) => <li key={h}>{h}</li>)}</ul>
        </section>
      </div>
    </>
  );
}
